import React, { useEffect, useMemo, useState } from "react";
import { Icon } from "@iconify/react";
import { Link } from "react-router-dom";
import { fmtNum, fmtDate, titleCase, toEpochMs } from "./copperConfig";
import { copperApi, COPPER_BASE } from "./copperApi";
import { useTimezone } from "../../hook/useTimezone";

const CopperSummary = () => {
  // Subscribe so the summary tables re-render when the user changes tz.
  useTimezone();
  const [summary, setSummary] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [tick, setTick] = useState(0);

  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    setError(null);
    copperApi
      .get("/api/summary")
      .then((res) => {
        if (cancelled) return;
        setSummary(res.data || null);
      })
      .catch((err) => {
        if (cancelled) return;
        const detail =
          err?.response?.data?.error ||
          err?.message ||
          "Failed to fetch Copper summary.";
        setError(typeof detail === "string" ? detail : JSON.stringify(detail));
      })
      .finally(() => !cancelled && setLoading(false));
    return () => { cancelled = true; };
  }, [tick]);

  const wallets = summary?.balances?.wallets || [];
  const transferOrders = summary?.transfers?.orders || [];
  const tradeOrders = summary?.orders?.orders || [];

  const stats = useMemo(() => {
    const currencies = new Set();
    let totalBalance = 0;
    let totalAvailable = 0;
    wallets.forEach((w) => {
      currencies.add(w.currency || w.mainCurrency);
      totalBalance += Number(w.balance || 0);
      totalAvailable += Number(w.available || 0);
    });

    const last7 = Date.now() - 7 * 24 * 60 * 60 * 1000;
    const recentTransfers = transferOrders.filter((o) => {
      const t = toEpochMs(o.createdAt);
      return Number.isFinite(t) && t >= last7;
    }).length;
    const recentTrades = tradeOrders.filter((o) => {
      const t = toEpochMs(o.createdAt);
      return Number.isFinite(t) && t >= last7;
    }).length;

    return {
      walletCount: wallets.length,
      currencyCount: currencies.size,
      totalBalance,
      totalAvailable,
      transferTotal: transferOrders.length,
      tradeTotal: tradeOrders.length,
      recentTransfers,
      recentTrades,
    };
  }, [wallets, transferOrders, tradeOrders]);

  const recentTransfers = useMemo(
    () =>
      [...transferOrders]
        .sort((a, b) => (toEpochMs(b.createdAt) || 0) - (toEpochMs(a.createdAt) || 0))
        .slice(0, 6),
    [transferOrders]
  );

  const recentTrades = useMemo(
    () =>
      [...tradeOrders]
        .sort((a, b) => (toEpochMs(b.createdAt) || 0) - (toEpochMs(a.createdAt) || 0))
        .slice(0, 6),
    [tradeOrders]
  );

  const topWallets = useMemo(
    () => [...wallets].sort((a, b) => Number(b.balance || 0) - Number(a.balance || 0)).slice(0, 6),
    [wallets]
  );

  if (loading) {
    return (
      <div className="cp-sum-wrap">
        <div className="cp-sum-grid">
          {Array.from({ length: 4 }).map((_, i) => (
            <div className="cp-sum-stat cp-skel-card" key={i} />
          ))}
        </div>
        {scopedStyles}
      </div>
    );
  }

  if (error) {
    return (
      <div className="cp-sum-wrap">
        <div className="cp-sum-error">
          <Icon icon="solar:danger-triangle-bold-duotone" />
          <div>
            <h6>Couldn't load Copper summary</h6>
            <p>{error}</p>
            <small>Verify the copper-api server is running at <code>{COPPER_BASE}</code>.</small>
          </div>
          <button className="cp-sum-btn" onClick={() => setTick((t) => t + 1)}>
            <Icon icon="solar:refresh-bold" /> Retry
          </button>
        </div>
        {scopedStyles}
      </div>
    );
  }

  return (
    <div className="cp-sum-wrap">
      {/* Top hero */}
      <div className="cp-sum-hero">
        <div className="cp-sum-hero-left">
          <div className="cp-sum-ico">
            <Icon icon="solar:pie-chart-2-bold-duotone" />
          </div>
          <div>
            <h4>Copper Snapshot</h4>
            <p>
              Live aggregate from <strong>{summary?.env || "copper"}</strong> environment.
              Fetched at {fmtDate(summary?.fetchedAt)}.
            </p>
          </div>
        </div>
        <button
          className="cp-sum-btn cp-sum-btn-primary"
          onClick={() => setTick((t) => t + 1)}
          title="Refresh snapshot"
        >
          <Icon icon="solar:refresh-bold" />
          Refresh
        </button>
      </div>

      {/* Stats */}
      <div className="cp-sum-grid">
        <StatCard
          icon="solar:wallet-money-bold-duotone"
          label="Wallets"
          value={stats.walletCount}
          tone="blue"
          sub={`${stats.currencyCount} unique currencies`}
          to="/copper/wallets"
        />
        <StatCard
          icon="solar:dollar-minimalistic-bold-duotone"
          label="Total Balance"
          value={fmtNum(stats.totalBalance)}
          tone="green"
          sub={`Available: ${fmtNum(stats.totalAvailable)}`}
        />
        <StatCard
          icon="solar:transfer-horizontal-bold-duotone"
          label="Transfers"
          value={stats.transferTotal}
          tone="purple"
          sub={`${stats.recentTransfers} in last 7 days`}
          to="/copper/transfers"
        />
        <StatCard
          icon="solar:chart-square-bold-duotone"
          label="Trade Orders"
          value={stats.tradeTotal}
          tone="orange"
          sub={`${stats.recentTrades} in last 7 days`}
          to="/copper/trades"
        />
      </div>

      {/* Two column listings */}
      <div className="cp-sum-cols">
        <Section title="Top Wallets" icon="solar:wallet-money-bold-duotone" link="/copper/wallets">
          {topWallets.length === 0 ? (
            <EmptyRow text="No wallet balances available." />
          ) : (
            <table className="cp-sum-table">
              <thead>
                <tr>
                  <th>Currency</th>
                  <th>Balance</th>
                  <th>Available</th>
                  <th>Portfolio</th>
                </tr>
              </thead>
              <tbody>
                {topWallets.map((w, i) => (
                  <tr key={(w.walletId || w.currency || "") + "-" + i}>
                    <td><strong>{w.currency || w.mainCurrency || "—"}</strong></td>
                    <td>{fmtNum(w.balance)}</td>
                    <td>{fmtNum(w.available)}</td>
                    <td>
                      <span className="cp-sum-tag">{w.portfolioType || "—"}</span>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </Section>

        <Section title="Recent Transfers" icon="solar:transfer-horizontal-bold-duotone" link="/copper/transfers">
          {recentTransfers.length === 0 ? (
            <EmptyRow text="No transfers yet." />
          ) : (
            <table className="cp-sum-table">
              <thead>
                <tr>
                  <th>Date</th>
                  <th>Direction</th>
                  <th>Currency</th>
                  <th>Amount</th>
                  <th>Status</th>
                </tr>
              </thead>
              <tbody>
                {recentTransfers.map((o, i) => (
                  <tr key={(o.orderId || "") + "-" + i}>
                    <td>{fmtDate(o.createdAt)}</td>
                    <td>
                      <span className={`cp-sum-side cp-sum-side-${o.orderType}`}>
                        {titleCase(o.orderType)}
                      </span>
                    </td>
                    <td>{o.baseCurrency || "—"}</td>
                    <td>{fmtNum(o.amount)}</td>
                    <td>{titleCase(o.status)}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </Section>
      </div>

      <div className="cp-sum-cols">
        <Section title="Recent Trade Orders" icon="solar:chart-square-bold-duotone" link="/copper/trades">
          {recentTrades.length === 0 ? (
            <EmptyRow text="No trades yet." />
          ) : (
            <table className="cp-sum-table">
              <thead>
                <tr>
                  <th>Date</th>
                  <th>Side</th>
                  <th>Pair</th>
                  <th>Amount</th>
                  <th>Price</th>
                  <th>Status</th>
                </tr>
              </thead>
              <tbody>
                {recentTrades.map((o, i) => (
                  <tr key={(o.orderId || "") + "-" + i}>
                    <td>{fmtDate(o.createdAt)}</td>
                    <td>
                      <span className={`cp-sum-side cp-sum-side-${o.orderType}`}>
                        {titleCase(o.orderType)}
                      </span>
                    </td>
                    <td>{o.baseCurrency}/{o.mainCurrency}</td>
                    <td>{fmtNum(o.amount)}</td>
                    <td>{fmtNum(o.price)}</td>
                    <td>{titleCase(o.status)}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </Section>

        <Section title="Quick Links" icon="solar:link-bold-duotone">
          <div className="cp-sum-quicklinks">
            <QuickLink to="/copper/wallets" icon="solar:wallet-money-bold-duotone" label="Wallets" hint="balances by portfolio" />
            <QuickLink to="/copper/portfolios" icon="solar:case-round-minimalistic-bold-duotone" label="Portfolios" hint="custody / trading" />
            <QuickLink to="/copper/orders" icon="solar:cart-large-2-bold-duotone" label="Orders" hint="buy / sell with date range" />
            <QuickLink to="/copper/transfers" icon="solar:transfer-horizontal-bold-duotone" label="Transfers" hint="deposits & withdrawals" />
            <QuickLink to="/copper/trades" icon="solar:chart-square-bold-duotone" label="Trades" hint="executed activity" />
            <QuickLink to="/copper/currencies" icon="solar:dollar-minimalistic-bold-duotone" label="Currencies" hint="supported assets" />
            <QuickLink to="/copper/addresses" icon="solar:map-point-bold-duotone" label="Addresses" hint="deposit / whitelist" />
          </div>
        </Section>
      </div>

      {scopedStyles}
    </div>
  );
};

const StatCard = ({ icon, label, value, sub, tone = "blue", to }) => {
  const card = (
    <div className={`cp-sum-stat cp-tone-${tone}`}>
      <div className="cp-sum-stat-ico">
        <Icon icon={icon} />
      </div>
      <div className="cp-sum-stat-body">
        <span className="cp-sum-stat-label">{label}</span>
        <strong className="cp-sum-stat-value">{value}</strong>
        {sub && <small className="cp-sum-stat-sub">{sub}</small>}
      </div>
      {to && <Icon icon="solar:arrow-right-up-linear" className="cp-sum-stat-arrow" />}
    </div>
  );
  return to ? <Link to={to} className="cp-sum-stat-link">{card}</Link> : card;
};

const Section = ({ title, icon, link, children }) => (
  <div className="cp-sum-sec">
    <div className="cp-sum-sec-head">
      <div>
        <Icon icon={icon} />
        <span>{title}</span>
      </div>
      {link && (
        <Link to={link} className="cp-sum-sec-link">
          View all <Icon icon="solar:arrow-right-linear" />
        </Link>
      )}
    </div>
    <div className="cp-sum-sec-body">{children}</div>
  </div>
);

const EmptyRow = ({ text }) => (
  <div className="cp-sum-empty">
    <Icon icon="solar:inbox-bold-duotone" /> {text}
  </div>
);

const QuickLink = ({ to, icon, label, hint }) => (
  <Link to={to} className="cp-sum-ql">
    <Icon icon={icon} />
    <div>
      <strong>{label}</strong>
      <small>{hint}</small>
    </div>
    <Icon icon="solar:arrow-right-linear" className="cp-sum-ql-arrow" />
  </Link>
);

const scopedStyles = (
  <style>{`
    .cp-sum-wrap { display: flex; flex-direction: column; gap: 18px; }

    .cp-sum-hero {
      display:flex; align-items:center; justify-content: space-between;
      gap: 14px; flex-wrap: wrap;
      background: linear-gradient(135deg, #eff6ff 0%, #ffffff 60%);
      border: 1px solid #e0ecff;
      border-radius: 18px;
      padding: 18px 22px;
    }
    .cp-sum-hero-left { display:flex; align-items:center; gap: 14px; }
    .cp-sum-ico {
      width: 52px; height: 52px;
      display:inline-flex; align-items:center; justify-content:center;
      border-radius: 14px;
      background: linear-gradient(135deg, #2563eb, #0ea5e9);
      color: #fff; font-size: 26px;
      box-shadow: 0 10px 24px rgba(37,99,235,0.25);
    }
    .cp-sum-hero h4 { margin: 0; font-size: 20px; color: #0f172a; font-weight: 700; }
    .cp-sum-hero p { margin: 4px 0 0; color: #475569; font-size: 13px; }

    .cp-sum-grid {
      display: grid;
      grid-template-columns: repeat(auto-fit, minmax(220px, 1fr));
      gap: 14px;
    }
    .cp-sum-stat-link { text-decoration: none; }
    .cp-sum-stat {
      display:flex; align-items:center; gap: 14px;
      background: #fff;
      border: 1px solid #eef2f7;
      border-radius: 16px;
      padding: 16px 18px;
      box-shadow: 0 4px 14px rgba(15,23,42,0.04);
      position: relative;
      transition: all .2s ease;
    }
    .cp-sum-stat-link:hover .cp-sum-stat {
      transform: translateY(-2px);
      box-shadow: 0 12px 22px rgba(37,99,235,0.12);
      border-color: #dbeafe;
    }
    .cp-sum-stat-ico {
      width: 48px; height: 48px;
      display:inline-flex; align-items:center; justify-content:center;
      border-radius: 12px;
      font-size: 22px;
      flex-shrink: 0;
    }
    .cp-tone-blue   .cp-sum-stat-ico { background: #eff6ff; color: #2563eb; }
    .cp-tone-green  .cp-sum-stat-ico { background: #ecfdf5; color: #16a34a; }
    .cp-tone-purple .cp-sum-stat-ico { background: #f5f3ff; color: #7c3aed; }
    .cp-tone-orange .cp-sum-stat-ico { background: #fff7ed; color: #ea580c; }

    .cp-sum-stat-body { display:flex; flex-direction: column; gap: 2px; min-width: 0; }
    .cp-sum-stat-label {
      font-size: 12px; color: #64748b;
      text-transform: uppercase; letter-spacing: 0.4px;
      font-weight: 600;
    }
    .cp-sum-stat-value { font-size: 22px; color: #0f172a; font-weight: 700; line-height: 1.1; }
    .cp-sum-stat-sub { font-size: 12px; color: #94a3b8; }
    .cp-sum-stat-arrow {
      position: absolute; top: 14px; right: 14px;
      color: #94a3b8; font-size: 16px;
    }

    .cp-skel-card {
      height: 92px;
      background: linear-gradient(90deg, #f1f5f9 0%, #e2e8f0 50%, #f1f5f9 100%);
      background-size: 200% 100%;
      animation: cp-sum-shimmer 1.4s linear infinite;
      border: none;
    }
    @keyframes cp-sum-shimmer {
      0% { background-position: 200% 0; }
      100% { background-position: -200% 0; }
    }

    .cp-sum-cols {
      display: grid;
      grid-template-columns: repeat(auto-fit, minmax(360px, 1fr));
      gap: 14px;
    }
    .cp-sum-sec {
      background: #fff;
      border: 1px solid #eef2f7;
      border-radius: 16px;
      overflow: hidden;
      box-shadow: 0 4px 14px rgba(15,23,42,0.04);
    }
    .cp-sum-sec-head {
      display:flex; align-items: center; justify-content: space-between;
      padding: 14px 18px;
      background: #fafbff;
      border-bottom: 1px solid #eef2f7;
    }
    .cp-sum-sec-head > div { display:flex; align-items:center; gap: 8px; color: #0f172a; font-weight: 600; }
    .cp-sum-sec-head svg { font-size: 18px; color: #2563eb; }
    .cp-sum-sec-link {
      display:inline-flex; align-items:center; gap: 4px;
      font-size: 12.5px; color: #1d4ed8; text-decoration: none;
      font-weight: 600;
    }
    .cp-sum-sec-link:hover { color: #1e3a8a; }
    .cp-sum-sec-body { padding: 6px 0; overflow-x: auto; }

    .cp-sum-table { width: 100%; border-collapse: separate; border-spacing: 0; font-size: 13px; }
    .cp-sum-table th {
      text-align: left;
      font-size: 11px;
      font-weight: 600;
      text-transform: uppercase;
      letter-spacing: 0.4px;
      color: #64748b;
      padding: 10px 16px;
      border-bottom: 1px solid #eef2f7;
      background: #fff;
      white-space: nowrap;
    }
    .cp-sum-table td {
      padding: 10px 16px;
      color: #1e293b;
      border-bottom: 1px solid #f1f5f9;
      white-space: nowrap;
    }
    .cp-sum-table tbody tr:last-child td { border-bottom: none; }
    .cp-sum-tag {
      display: inline-block;
      padding: 2px 10px;
      border-radius: 999px;
      background: #eff6ff;
      color: #1d4ed8;
      font-size: 11.5px;
      font-weight: 600;
      text-transform: capitalize;
    }
    .cp-sum-side {
      display: inline-block;
      padding: 2px 10px;
      border-radius: 999px;
      font-size: 11.5px;
      font-weight: 700;
      text-transform: capitalize;
    }
    .cp-sum-side-buy, .cp-sum-side-deposit {
      background: #ecfdf5; color: #16a34a;
    }
    .cp-sum-side-sell, .cp-sum-side-withdraw {
      background: #fef2f2; color: #dc2626;
    }

    .cp-sum-empty {
      padding: 30px 16px;
      text-align: center;
      color: #94a3b8;
      font-size: 13px;
    }
    .cp-sum-empty svg { font-size: 22px; vertical-align: middle; margin-right: 4px; color: #cbd5e1; }

    .cp-sum-quicklinks {
      display: grid;
      grid-template-columns: repeat(auto-fit, minmax(180px, 1fr));
      gap: 10px;
      padding: 14px;
    }
    .cp-sum-ql {
      display:flex; align-items:center; gap: 10px;
      padding: 12px 14px;
      background: #fafbff;
      border: 1px solid #eef2f7;
      border-radius: 12px;
      text-decoration: none;
      transition: all .2s ease;
      color: #0f172a;
    }
    .cp-sum-ql:hover {
      background: #eff6ff;
      border-color: #bfdbfe;
      transform: translateY(-1px);
    }
    .cp-sum-ql > svg:first-child { font-size: 22px; color: #2563eb; flex-shrink: 0; }
    .cp-sum-ql > div { display:flex; flex-direction:column; min-width: 0; flex: 1; }
    .cp-sum-ql strong { font-size: 13.5px; font-weight: 600; }
    .cp-sum-ql small { font-size: 11.5px; color: #64748b; }
    .cp-sum-ql-arrow { font-size: 16px; color: #94a3b8; }

    .cp-sum-btn {
      display:inline-flex; align-items:center; gap: 6px;
      padding: 8px 14px;
      border: 1.5px solid #dbeafe;
      border-radius: 10px;
      font-size: 13px;
      font-weight: 600;
      cursor: pointer;
      background: #fff;
      color: #1d4ed8;
      transition: all .2s ease;
    }
    .cp-sum-btn:hover { background: #eff6ff; border-color: #93c5fd; }
    .cp-sum-btn svg { font-size: 16px; }
    .cp-sum-btn-primary {
      color: #fff;
      background: linear-gradient(135deg, #2563eb 0%, #1d4ed8 60%, #0ea5e9 100%);
      background-size: 200% 200%;
      background-position: 0% 50%;
      border-color: transparent;
      box-shadow: 0 8px 16px rgba(37, 99, 235, 0.25);
    }
    .cp-sum-btn-primary:hover {
      background-position: 100% 50%;
      transform: translateY(-1px);
      color: #fff;
    }

    .cp-sum-error {
      display:flex; align-items: center; gap: 14px; flex-wrap: wrap;
      background: #fff;
      border: 1px solid #fee2e2;
      border-radius: 16px;
      padding: 22px 24px;
    }
    .cp-sum-error > svg { font-size: 36px; color: #ef4444; }
    .cp-sum-error h6 { margin: 0; font-size: 15px; color: #0f172a; font-weight: 600; }
    .cp-sum-error p { margin: 4px 0; font-size: 13px; color: #64748b; word-break: break-word; }
    .cp-sum-error small { font-size: 12px; color: #94a3b8; }
    .cp-sum-error code { background: #f1f5f9; padding: 1px 6px; border-radius: 4px; font-size: 11.5px; color: #1d4ed8; }
  `}</style>
);

export default CopperSummary;
